import React, { useState, useEffect, useRef } from 'react';

const remainingFrom = (expiresAt) => {
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.round((deadline - Date.now()) / 1000));
};

export const formatCountdown = (seconds) => {
  const safe = Math.max(0, seconds);
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

/**
 * A self-contained M:SS countdown.
 *
 * The per-second state lives here, at the only node that displays it. That is
 * the whole point of the component: previously this state sat on the
 * verification page, so every tick re-rendered the client picker, the code
 * boxes and all 25 history rows (with an Intl date format each) for the sake of
 * one text node — roughly 600 full-page renders over a code's ten-minute life.
 *
 * Do not lift `secondsLeft` back into the parent. The parent needs one
 * transition (expired / not expired), not six hundred numbers.
 *
 * @param {string} expiresAt ISO-8601 instant. Must be a stable primitive — a
 *   fresh `new Date(...)` each render would re-arm the interval every render.
 * @param {Function} [onExpire] Called at most once per distinct `expiresAt`,
 *   when the remaining time first reaches zero (possibly synchronously on
 *   mount, if the instant is already past). Read through a ref, so an unstable
 *   identity will not restart the timer.
 * @param {string} [label]
 * @param {number} [warnAtSeconds] Below this, the time turns red.
 */
const Countdown = ({ expiresAt, onExpire, label = 'Expires in', warnAtSeconds = 60 }) => {
  const [secondsLeft, setSecondsLeft] = useState(() => remainingFrom(expiresAt));
  const onExpireRef = useRef(onExpire);

  // No dependency array on purpose: keep the ref current without re-arming the
  // interval below, whose effect must depend only on `expiresAt`.
  useEffect(() => {
    onExpireRef.current = onExpire;
  });

  useEffect(() => {
    const deadline = new Date(expiresAt).getTime();
    if (Number.isNaN(deadline)) return undefined;

    // Scoped to this effect run, so a new expiresAt re-arms the latch
    // automatically and nothing has to reset it by hand.
    let fired = false;
    let timer = null;

    const tick = () => {
      const remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0 && !fired) {
        fired = true;
        // Nothing left to count. Stopping here also means there is no later
        // tick that could fire onExpire a second time.
        if (timer) clearInterval(timer);
        onExpireRef.current?.();
      }
    };

    tick();
    if (!fired) {
      timer = setInterval(tick, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [expiresAt]);

  return (
    <div className="text-end">
      <div className="text-muted small">{label}</div>
      <div className={`fs-5 ${secondsLeft <= warnAtSeconds ? 'text-danger' : ''}`}>
        {formatCountdown(secondsLeft)}
      </div>
    </div>
  );
};

export default Countdown;
